import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { zustandSafeLocalStorage } from './safeLocalStorage';

const todayFormatted = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function normalizePersistedDate(value: unknown, fallback: string): string {
    if (typeof value !== 'string' || value.length > 32 || !ISO_DATE.test(value)) return fallback;
    return value;
}

type TrackerPersisted = {
    startDate: string;
    endDate: string;
};

interface TrackerState {
    startDate: string;
    endDate: string;
    setStartDate: (date: string) => void;
    setEndDate: (date: string) => void;
    resetDates: (minDate: string, maxDate: string) => void;
    resetToDefaults: () => void;
}

const trackerStorage = createJSONStorage<TrackerPersisted>(() => zustandSafeLocalStorage);

export const useTrackerStore = create<TrackerState>()(
    persist(
        (set) => ({
            startDate: '2026-01-01',
            endDate: todayFormatted(),

            setStartDate: (date) => set({ startDate: date }),
            setEndDate: (date) => set({ endDate: date }),

            resetDates: (minDate, maxDate) => set({ startDate: minDate, endDate: maxDate }),

            resetToDefaults: () =>
                set({ startDate: '2026-01-01', endDate: todayFormatted() }),
        }),
        {
            name: 'tracker-date-filter',
            version: 1,
            storage: trackerStorage,
            partialize: (state): TrackerPersisted => ({
                startDate: state.startDate,
                endDate: state.endDate,
            }),
            merge: (persistedState, currentState) => {
                const p = persistedState as Partial<TrackerPersisted> | null | undefined;
                if (!p) return currentState;
                const startFallback = currentState.startDate;
                const endFallback = currentState.endDate;
                return {
                    ...currentState,
                    startDate: normalizePersistedDate(p.startDate, startFallback),
                    endDate: normalizePersistedDate(p.endDate, endFallback),
                };
            },
            migrate: (persisted): TrackerPersisted => {
                const p = persisted as Partial<TrackerPersisted> | null | undefined;
                const start = normalizePersistedDate(p?.startDate, '2026-01-01');
                const end = normalizePersistedDate(p?.endDate, todayFormatted());
                return { startDate: start, endDate: end };
            },
        }
    )
);
